import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const carouselImages = [
  { src: '/img/sarah-mitchel2-page-en.png', alt: 'Sarah Mitchell' },
  { src: '/img/alex-murphy-page-en.png', alt: 'Alex Murphy' },
  { src: '/img/olivia-dunham-page-en.png', alt: 'Olivia Dunham' },
  { src: '/img/daniel-jackson-page-en.png', alt: 'Daniel Jackson' },
  { src: '/img/mauris-porta-page-en.png', alt: 'Mauris Porta' },
  { src: '/img/cayce-pollard-page-en.png', alt: 'Cayce Pollard' },
];

export function FeatureCardsSection() {
  const { t } = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);
  const simpleCardRef = useRef<HTMLDivElement>(null);
  const responsiveCardRef = useRef<HTMLDivElement>(null);
  const freeCardRef = useRef<HTMLDivElement>(null);
  const phonesContainerRef = useRef<HTMLDivElement>(null);
  const cardFanRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            entry.target.querySelectorAll('.phone-stagger').forEach((el) => {
              el.classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    [simpleCardRef, responsiveCardRef, freeCardRef, phonesContainerRef, cardFanRef].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  // Carousel auto-rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="space-y-12">
          {/* Example 2 - Simple */}
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="relative order-2 md:order-1">
              <div
                ref={simpleCardRef}
                className="rounded-xl overflow-hidden shadow-2xl p-6 animated-gradient scroll-animate"
              >
                <div className="fade-carousel aspect-[4/3]">
                  {carouselImages.map((image, index) => (
                    <div
                      key={index}
                      className={`fade-carousel-slide ${index === activeSlide ? 'active' : ''}`}
                    >
                      <img src={image.src} alt={image.alt} />
                    </div>
                  ))}
                  <div className="fade-carousel-indicators">
                    {carouselImages.map((_, index) => (
                      <button
                        key={index}
                        className={`fade-carousel-dot ${index === activeSlide ? 'active' : ''}`}
                        onClick={() => setActiveSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center md:text-left order-1 md:order-2">
              <h3 className="text-2xl mb-3">{t('simple.title')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('simple.description')}
              </p>
            </div>
          </div>

          {/* Example 3 - Responsive */}
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-center md:text-left">
              <h3 className="text-2xl mb-3">{t('responsive.title')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('responsive.description')}
              </p>
            </div>
            <div className="relative">
              <div
                ref={responsiveCardRef}
                className="rounded-xl overflow-hidden shadow-2xl p-6 animated-gradient scroll-animate"
              >
                <div ref={phonesContainerRef} className="flex gap-3 justify-center">
                  <div className="w-28 rounded-lg overflow-hidden shadow-lg phone-mockup floating phone-stagger">
                    <img src="/img/alex-murphy-iphone.png" alt="Alex Murphy iPhone" className="w-full h-auto" />
                  </div>
                  <div className="w-28 rounded-lg overflow-hidden shadow-lg phone-mockup floating phone-stagger">
                    <img src="/img/olivia-dunham-iphone.png" alt="Olivia Dunham iPhone" className="w-full h-auto" />
                  </div>
                  <div className="w-28 rounded-lg overflow-hidden shadow-lg phone-mockup floating phone-stagger">
                    <img src="/img/landing-page-1-iphone.png" alt="Landing Page iPhone" className="w-full h-auto" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Example 4 - Free */}
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="relative order-2 md:order-1">
              <div
                ref={freeCardRef}
                className="rounded-xl overflow-hidden shadow-2xl p-8 animated-gradient-alt scroll-animate"
              >
                <div className="h-64 flex items-center justify-center">
                  {/* Fan Spread Cards */}
                  <div ref={cardFanRef} className="card-fan-container">
                    <div className="card-fan-item">
                      <img src="/img/alex-murphy-page-en.png" alt="Alex Murphy" />
                    </div>
                    <div className="card-fan-item">
                      <img src="/img/olivia-dunham-page-en.png" alt="Olivia Dunham" />
                    </div>
                    <div className="card-fan-item">
                      <img src="/img/daniel-jackson-page-en.png" alt="Daniel Jackson" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center md:text-left order-1 md:order-2">
              <h3 className="text-2xl mb-3">{t('free.title')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('free.description')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
